import React, {forwardRef, useState} from 'react';
import {Platform, StyleProp, StyleSheet, TextInput as RNTextInput, TextStyle} from 'react-native';
import {Input as RNEInput, InputProps as RNEInputProps} from '@rn-vui/themed';

import {fontFamilyStyle} from '../Text';

export type TextInputProps = RNEInputProps & {
  touched?: boolean;
  errorMessageWhenTouched?: string;
  inputFocusStyle?: StyleProp<TextStyle>;
  /**
   * When true on web, forces the inner input font-size to 16px to prevent
   * mobile-browser focus auto-zoom (iOS Safari / Chromium iOS). Opt-in;
   * defaults to false so all other TextInput consumers are unaffected.
   * See DIS-1345.
   */
  webMinFontSize?: boolean;
};

// RNE Input wraps React Native TextInput, so the ref should expose TextInput methods
// Using React Native TextInput ref type since RNE Input forwards to it
export type TextInputRef = React.ElementRef<typeof RNTextInput>;

const commonTextStyles = {
  ...fontFamilyStyle,
};

const TextInput = forwardRef<TextInputRef, TextInputProps>(
  (
    {
      autoCorrect = false,
      errorMessage,
      errorMessageWhenTouched,
      renderErrorMessage = true,
      autoCapitalize = 'none',
      returnKeyLabel = 'next',
      returnKeyType = 'next',
      touched,
      inputStyle,
      inputFocusStyle,
      webMinFontSize = false,
      value,
      defaultValue,
      ...rest
    }: TextInputProps,
    ref,
  ) => {
    // Display error message when field is touched (React Hook Form pattern)
    let errorMessageOrPlaceholder = errorMessage;
    if (touched && errorMessageWhenTouched) {
      errorMessageOrPlaceholder = errorMessageWhenTouched;
    }
    if (renderErrorMessage && !errorMessageOrPlaceholder) {
      errorMessageOrPlaceholder = ' ';
    }

    const [focused, setFocused] = useState(false);

    const webMinFontSizeStyle =
      webMinFontSize && Platform.OS === 'web' ? styles.webMinFontSize : undefined;

    return (
      <RNEInput
        {...rest}
        onBlur={e => {
          setFocused(false);
          rest.onBlur?.(e);
        }}
        onFocus={e => {
          setFocused(true);
          rest.onFocus?.(e);
        }}
        errorStyle={commonTextStyles}
        labelStyle={commonTextStyles}
        inputStyle={[
          commonTextStyles,
          styles.input,
          webMinFontSizeStyle,
          inputStyle,
          focused && inputFocusStyle,
        ]}
        autoCapitalize={autoCapitalize}
        autoCorrect={autoCorrect}
        returnKeyLabel={returnKeyLabel}
        returnKeyType={returnKeyType}
        errorMessage={errorMessageOrPlaceholder}
        ref={ref}
        value={value !== undefined ? value : undefined}
        defaultValue={value === undefined && defaultValue !== undefined ? defaultValue : undefined}
        renderErrorMessage={renderErrorMessage}
      />
    );
  },
);

const styles = StyleSheet.create({
  input: {
    paddingHorizontal: 4,
  },
  webMinFontSize: {
    fontSize: 16,
  },
});

export default TextInput;
